/**
 *  AZ OSZTÁLYFŐNÖKI JEGY-MÁTRIX SZÁMÍTÁSAI – tiszta függvények, a komponenstől külön.
 *
 *  A szabályok a szülői jegy-nézetével és az Értékelések menüével egyeznek (lásd
 *  `szuloi-jegyek`, `ertekelesek`, `ertekeles-modositas.ts`): ha az osztályfőnök más
 *  átlagot látna, mint a szülő vagy a szaktanár, három képernyő három igazságot mondana
 *  ugyanarról a gyerekről.
 *    – a záró (félévi, év végi) és a negyedéves értékelés NEM számít az átlagba:
 *      összegzi a jegyeket, nem újabb jegy;
 *    – a súly az értékelés módjának szorzója (100 = egyszeres, 200 = dupla). A 0-s szorzó
 *      („Diagnosztikus mérés (0x)") itt NEM számít az átlagba, ahogy a KRÉTA-ban sem – a
 *      szülői és az Értékelések-nézet ezt ma még egyszeresnek veszi (2026-09: élesben nincs
 *      ilyen jegy, így eltérést most nem okoz);
 *    – a „%" csak a százalékos jegyek átlaga mögé jár.
 */
import { NEGYEDEV_JEL, negyedevesE, strukturaltRovid } from '../ertekeles-modositas';

export type OfIdoszak = 'ev' | 'f1' | 'f2';

export interface OfJegy {
  id: string;
  oktazon: string;
  tantargy_id: string;
  jegy: string;
  datum: string;
  tema: string | null;
  tipus: number;
  modja: number;
  ertnev?: string | null;
  ertek_szam?: number | null;
  ertek_forma?: string | null;
  dicseretes?: number;
  tanar?: string;
  //  A felületen kiegészítve – a szülői nézet jegy-alakja szerint.
  emnev?: string;
  szin?: string | null;
  szorzo?: number | null;
  tanar_nev?: string;
  rovid?: string;
  jel?: string;          // N1–N4 (negyedéves)
  zaro?: '' | 'F' | 'É'; // félévi / év végi
  felev?: 'f1' | 'f2';
  szinCss?: string | null;
  sulyOsztaly?: '' | 'dupla' | 'nulla';
  cim?: string;          // az egérrel ráállva megjelenő részletek
}

export interface OfAtlag { atlag: number | null; db: number; szazalek: boolean; }

/** A jegy rövid alakja: záró/negyedévesnél „5D", „85%", egyébként a beírt érték röviden. */
export function jegyRovid(item: any): string {
  const strukturalt = strukturaltRovid(item);
  if (strukturalt) return strukturalt;
  const value = String(item?.jegy ?? '').trim();
  const fokozat: Record<string, string> = { 'Példás': 'P', 'Jó': 'J', 'Változó': 'V', 'Rossz': 'H', 'Hanyag': 'H', 'R': 'H' };
  //  A „/" csak a magatartás/szorgalom-párnál bontó jel (pl. „Példás / Jó").
  if (value.includes('/')) {
    const reszek = value.split('/').map((r) => r.trim());
    if (reszek.every((r) => !!fokozat[r] || r.length <= 4)) return reszek.map((r) => fokozat[r] || r).join('/');
  }
  if (fokozat[value]) return fokozat[value];
  //  Szám (pl. NKP-eredmény „48.5%”) sosem „SZ”: az a hosszú szöveges értékelés rövidítése.
  if (/^-?\d+([.,]\d+)?\s*%?$/.test(value)) return value.replace(/^(-?\d+)[.,]\d+/, '$1').replace(/\s+/g, '');
  return value.length > 4 ? 'SZ' : value;
}

export function zaroJel(item: any): '' | 'F' | 'É' {
  const m = String(item?.modja ?? '').trim();
  return m === '2' ? 'F' : m === '3' ? 'É' : '';
}

/** A súly (1 = egyszeres). A szorzó százalékban jön (100, 200, 0). */
export function jegySuly(item: any): number {
  const raw = item?.szorzo;
  if (raw === undefined || raw === null || raw === '') return 1;
  const value = typeof raw === 'number' ? raw : Number(String(raw).trim().replace(',', '.').replace('%', ''));
  if (!Number.isFinite(value) || value < 0) return 1;
  return value > 10 ? value / 100 : value;
}

function jegySzam(item: any): number | null {
  const e = item?.jegy;
  if (e === undefined || e === null) return null;
  const raw = String(e).trim();
  //  A „/"-es (magatartás/szorgalom) és a számjegy nélküli (szöveges, „–") érték nem szám.
  if (!raw || raw.includes('/')) return null;
  const m = raw.match(/-?\d+(?:[.,]\d+)?/);
  if (!m) return null;
  const n = Number(m[0].replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export function atlagSzamol(items: OfJegy[]): OfAtlag {
  const ertekek = (items || [])
    .filter((i) => !zaroJel(i) && !negyedevesE(i))
    .map((i) => ({
      v: jegySzam(i), w: jegySuly(i),
      sz: i?.ertek_forma === 'szazalek' || String(i?.jegy ?? '').trim().endsWith('%'),
    }))
    .filter((x) => x.v !== null && Number.isFinite(x.v) && x.w > 0) as { v: number; w: number; sz: boolean }[];
  if (!ertekek.length) return { atlag: null, db: 0, szazalek: false };
  const suly = ertekek.reduce((s, x) => s + x.w, 0);
  const atlag = ertekek.reduce((s, x) => s + x.v * x.w, 0) / suly;
  return { atlag, db: ertekek.length, szazalek: ertekek.every((x) => x.sz) };
}

export function atlagSzoveg(a: OfAtlag | null | undefined, tizedes = 1): string {
  if (!a || a.atlag === null || !Number.isFinite(a.atlag)) return '';
  const s = a.atlag.toFixed(tizedes).replace('.', ',');
  return a.szazalek ? `${s}%` : s;
}

/** Gyenge-e az átlag: érdemjegyeknél 2,0 alatt, százalékosnál 50% alatt. */
export function gyengeAtlag(a: OfAtlag | null | undefined): boolean {
  if (!a || a.atlag === null) return false;
  return a.szazalek ? a.atlag < 50 : a.atlag < 2;
}

/*  A FEKETE SZÍN NEM SZÍN. A típusok egy része „#000000"-t tárol – sötét háttéren az
 *  olvashatatlan volna, világoson pedig semmit nem mond. Ilyenkor a téma szövegszíne marad. */
export function lathatoSzin(szin: any): string | null {
  const s = String(szin ?? '').trim().toLowerCase().replace(/\s+/g, '');
  if (!s || ['#000', '#000000', 'black', 'rgb(0,0,0)'].includes(s)) return null;
  return String(szin).trim();
}

/** Melyik félévbe esik a nap. A határ a tanév-tábla félév-dátuma, ha nincs: január 19. */
export function felevE(datum: string, felevVege: string): 'f1' | 'f2' {
  return String(datum || '').slice(0, 10) <= felevVege ? 'f1' : 'f2';
}

/**
 *  A kiszolgáló normalizált válaszából a szülői nézet jegy-alakja: típusnév, szín, súly,
 *  tanárnév – és a megjelenítéshez előre kiszámolt rövid alak, jelölők, felugró szöveg.
 *  Egyszer fut a betöltéskor, nem a sablonban minden változás-ellenőrzéskor.
 */
export function jegyekKiegeszit(adat: any): OfJegy[] {
  const modok = new Map<string, any>((adat?.modok || []).map((m: any) => [String(m.id), m]));
  const tanarok: Record<string, string> = adat?.tanarok || {};
  const tanevId = Number(adat?.tanev?.id);
  const felevVege = String(adat?.tanev?.felev || (Number.isFinite(tanevId) ? `${tanevId + 1}-01-19` : '')).slice(0, 10);
  return (adat?.jegyek || []).map((j: any) => {
    const mod = modok.get(String(j.tipus));
    const item: OfJegy = {
      ...j,
      emnev: mod?.nev || '',
      szin: mod?.szin ?? null,
      szorzo: mod?.szorzo ?? null,
      tanar_nev: tanarok[String(j.tanar || '')] || j.tanar || '',
    };
    item.rovid = jegyRovid(item);
    item.jel = NEGYEDEV_JEL[String(item.modja)] || '';
    item.zaro = zaroJel(item);
    item.felev = felevVege ? felevE(item.datum, felevVege) : 'f1';
    item.szinCss = lathatoSzin(item.szin);
    const suly = jegySuly(item);
    item.sulyOsztaly = item.zaro || item.jel ? '' : suly >= 2 ? 'dupla' : suly === 0 ? 'nulla' : '';
    const sorok = [
      `${item.datum}${item.zaro ? ` · ${item.zaro === 'F' ? 'félévi' : 'év végi'} értékelés` : ''}${item.jel ? ` · ${item.ertnev || 'negyedéves'}` : ''}`,
      String(item.jegy ?? '').trim().length > 4 ? `Értékelés: ${item.jegy}` : '',
      item.emnev ? `Típus: ${item.emnev}` : '',
      item.szorzo !== null && item.szorzo !== undefined && !item.zaro ? `Súly: ${item.szorzo}%` : '',
      item.tema ? `Téma: ${item.tema}` : '',
      item.tanar_nev ? `Tanár: ${item.tanar_nev}` : '',
    ];
    item.cim = sorok.filter(Boolean).join('\n');
    return item;
  });
}
